import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { setCredentials } from '../../redux/slices/authSlice';
import {
  useLinkOpenAIMutation,
  useUnlinkOpenAIMutation,
  useLinkPerplexityMutation,
  useUnlinkPerplexityMutation
} from '../../redux/slices/profileApiSlice';
import {
  LinkOpenAIInitialValues,
  LinkOpenAISchema,
  LinkPerplexityInitialValues,
  LinkPerplexitySchema
} from './helper';
import { IntegrationCard } from '../../shared/components/profile';

const Integrations = () => {
  const { t } = useTranslation();

  // Integration functions
  const [linkOpenAI, { isLoading: isLinkingOpenAI }] = useLinkOpenAIMutation();
  const [unlinkOpenAI, { isLoading: isUnlinkingOpenAI }] = useUnlinkOpenAIMutation();
  const [linkPerplexity, { isLoading: isLinkingPerplexity }] = useLinkPerplexityMutation();
  const [unlinkPerplexity, { isLoading: isUnlinkingPerplexity }] = useUnlinkPerplexityMutation();

  const dispatch = useDispatch();
  const { userInfo, accessToken } = useSelector((state) => state.auth);

  const integrations = [
    {
      name: 'OpenAI',
      platform: 'openai',
      apiKey: userInfo?.openai_api_key || '',
      requiresFunding: userInfo?.openai_requires_funding,
      apiKeyName: "openai_api_key",
      placeholder: 'sk-...',
      text: t("openai-text", "Utilisez les différents modèles d'OpenAI pour générer du texte."),
      importance: "Requis",
      link: window.location.origin + "/docs?menu=integrations&submenu=openai",
      initialValues: LinkOpenAIInitialValues,
      validationSchema: LinkOpenAISchema,
      linkPlatform: linkOpenAI,
      linkLoading: isLinkingOpenAI,
      unlinkPlatform: unlinkOpenAI,
      unlinkLoading: isUnlinkingOpenAI
    },
    {
      name: 'Perplexity',
      platform: 'perplexity',
      apiKey: userInfo?.perplexity_api_key || '',
      requiresFunding: userInfo?.perplexity_requires_funding,
      apiKeyName: "perplexity_api_key",
      placeholder: 'pplx-...',
      text: t("perplexity-text", "Utilisez les différents modèles de Perplexity pour générer du texte."),
      importance: "Recommandé",
      link: window.location.origin + "/docs?menu=integrations&submenu=perplexity",
      initialValues: LinkPerplexityInitialValues,
      validationSchema: LinkPerplexitySchema,
      linkPlatform: linkPerplexity,
      linkLoading: isLinkingPerplexity,
      unlinkPlatform: unlinkPerplexity,
      unlinkLoading: isUnlinkingPerplexity
    },
  ];

  const handleLinkIntegration = async (integration, values, actions) => {
    try {
      const res = await integration.linkPlatform({ [integration.apiKeyName]: values.apiKey });
      if (res?.data?.status === 200) {
        const updatedUserInfo = {
          ...userInfo,
          [`${integration.platform}_api_key`]: res?.data?.data?.[`${integration.platform}_api_key`],
          [`${integration.platform}_requires_funding`]: false
        };
        dispatch(setCredentials({ accessToken, userInfo: updatedUserInfo }));
        toast.success(t(`integrated-${integration.name}-key`, `La clé ${integration.name} a été intégrée avec succès !`));
      } else {
        toast.error(res?.data?.message || 'Erreur inconnue');
      }
    } catch (error) {
      toast.error(error?.data?.detail || 'Une erreur est survenue');
    } finally {
      actions.setSubmitting(false);
    }
  };
  
  const handleUnlinkIntegration = async (integration) => {
    try {
      const res = await integration.unlinkPlatform();
      if (res?.data?.status === 200) {
        const updatedUserInfo = {
          ...userInfo,
          [`${integration.platform}_api_key`]: null,
          [`${integration.platform}_requires_funding`]: false
        };
        dispatch(setCredentials({ accessToken, userInfo: updatedUserInfo }));
        toast.success(t(`disconnected-${integration.name}-key`, `La clé ${integration.name} a été déconnectée avec succès !`));
      } else {
        toast.error(res?.data?.message || 'Erreur inconnue');
      }
    } catch (error) {
      toast.error(error?.data?.detail || 'Une erreur est survenue');
    }
  };

  return (
    <div className="mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
      {integrations.map((integration) => (
        <IntegrationCard
          key={integration.platform}
          integration={integration}
          onLink={handleLinkIntegration}
          onUnlink={handleUnlinkIntegration}
        />
      ))}
    </div>
  );
};

export default Integrations;